import { motion, useAnimation } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import {
  selectedSlideSelector,
  TemplateStateActions,
  useDesignerDataContext,
} from '../../../hooks/useDesignerLogic';
import {
  DesignerUIStateActions,
  GeneratorTabState,
  setSelectedTab,
  useDesignerUIContext,
} from '../../../hooks/useDesignerUIState';
import { useMetrics } from '../../../hooks/useMetrics';
import { useOnOutsideClick } from '../../../hooks/useOnOutsideClick';
import useTabsSelector, { TabSelector } from '../../../hooks/useTabsSelector';
import { RoundMedia } from '../../../services/backendService/types';
import { zIndexes } from '../../../utils/zIndexes';
import BottomDrawerDragHandle from '../../_molecules/BottomDrawerDragHandle';
import { Slide } from '../../SlideComponents/slide.types';
import BackgroundTab from '../DesignerTabPane/BackgroundTab';
import ColorPickerTab from '../DesignerTabPane/ColorPickerTab';
import FigureElementTab from '../DesignerTabPane/FigureElementTab';
import GeneratorsTab from '../DesignerTabPane/GeneratorsTab';
import InteractiveElementsTab from '../DesignerTabPane/InteractiveElements';
import MediaTab from '../DesignerTabPane/MediaTab';
import TemplatesTab from '../DesignerTabPane/TemplatesTab';
import TextTab from '../DesignerTabPane/TextTab';
import TranslateTab from '../DesignerTabPane/TranslateTab';
import UploadsTab from '../DesignerTabPane/UploadsTab';
import { TabSelectorButton } from './DesignerTabUtils';

const DesignerMobileTabs = ({
  templateId,
  setGeneratorTabState,
  setUploadedSlidesCache,
}: {
  templateId: string;
  setGeneratorTabState: (generatorTabState: GeneratorTabState) => void;
  setUploadedSlidesCache: (rounds: Slide[] | undefined) => void;
}) => {
  const dispatch = useDesignerUIContext(context => context.dispatch);
  const dataDispatch = useDesignerDataContext(context => context.dispatch);

  const selectedTab = useDesignerUIContext(context => context.state.selectedTab);

  const onCloseDrawer = useDesignerUIContext(context => context.handleCloseDrawer);

  const isDrawerOpen = useDesignerUIContext(context => context.state.isDrawerOpen);

  const droppedImageData = selectedTab.initialData?.droppedImageData;

  const onSlideChange = useDesignerDataContext(context => context.handleSlideChange);

  const selectedSlide = useDesignerDataContext(selectedSlideSelector);

  const slides = useDesignerDataContext(
    context => context.state.localTemplate?.content.slides || [],
  );

  const lessonAttachments = useDesignerDataContext(
    context => context.state.localTemplate?.attachments,
  );

  const aiContent = useDesignerDataContext(
    context => context.state.localTemplate?.aiContent,
  );

  const handleSlideTypeChanged = useDesignerDataContext(
    context => context.handleSlideTypeChanged,
  );

  const selectedElements = useDesignerUIContext(
    context => context.state.selectedElements,
  );
  const setSelectedElements = useDesignerUIContext(
    context => context.setSelectedElements,
  );

  const onAllRoundChange = useDesignerDataContext(
    context => context.handleAllSlideChange,
  );

  const handleCreateNewSlide = useDesignerDataContext(
    context => context.handleCreateNewSlide,
  );

  const onElementsChange = useDesignerDataContext(
    context => context.handleElementsChange,
  );

  const onAddSlide = useDesignerDataContext(context => context.addSlide);
  const addTranslatedSlides = useDesignerDataContext(
    context => context.addTranslatedSlides,
  );

  const onDroppedImageDataConsumed = useCallback(() => {
    dispatch({
      type: DesignerUIStateActions.clearDroppedImageData,
    });
  }, [dispatch]);

  const setBackgroundForAllRounds = useCallback(
    (media: RoundMedia) => {
      dataDispatch({ type: TemplateStateActions.setBackgroundOnAllSlides, media });
    },
    [dataDispatch],
  );

  const { t } = useTranslation('Host');
  const metrics = useMetrics();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [searchTemplatesValue, setSearchTemplatesValue] = useState<string | undefined>();

  const handleAIGeneratedRoundCreated = (rounds: Slide[]) => {
    onAddSlide(rounds);
    setSelectedTab(dispatch, { tab: 'interactiveElements' });
  };

  const tabSelector = useTabsSelector();
  useOnOutsideClick(
    containerRef,
    () => {
      onCloseDrawer();
    },
    {
      classNameMatch: [
        'roundTypeSelectorMenuItem',
        'globalShareModal',
        'editorTab',
        'infinite-viewer-scroll-area',
        'generatorsFilterSelector',
        'ant-select-dropdown', // For the dropdowns in the generators tab
      ],
    },
  );

  const controls = useAnimation();

  useEffect(() => {
    controls.start(
      isDrawerOpen
        ? { height: '80%', transition: { ease: 'easeInOut' } }
        : { height: '70px', transition: { ease: 'easeInOut' } },
    );
  }, [isDrawerOpen, controls]);

  const height = isDrawerOpen ? '80%' : '70px';
  const handleTabChange = useCallback(
    (button: TabSelector) => {
      metrics.logEvent('Creator.ChangeTab', { selectedTab });
      if (isDrawerOpen && selectedTab.tab === button.tabTitle) {
        onCloseDrawer();
        return;
      }
      setSelectedTab(dispatch, { tab: button.tabTitle });
    },
    [dispatch, isDrawerOpen, metrics, onCloseDrawer, selectedTab],
  );
  return (
    <FloatingContainer
      drag="y"
      dragElastic={0}
      dragConstraints={{
        bottom: -height,
        top: 0,
        left: 0,
        right: 0,
      }}
      dragSnapToOrigin
      dragMomentum={false}
      animate={controls}
    >
      {isDrawerOpen && <BottomDrawerDragHandle onClose={() => onCloseDrawer()} />}
      {isDrawerOpen && (
        <TabPaneContainer ref={containerRef}>
          <div style={{ height: 10, width: '100%' }}>
            {/* Works as a padding, but is preventing a small animation flicker */}
          </div>
          {selectedTab.tab === 'generators' && (
            <GeneratorsTab
              onAddActivity={handleAIGeneratedRoundCreated}
              selectedSubTab={selectedTab.subTab}
              handleCreateNewSlide={handleCreateNewSlide}
              setGeneratorTabState={setGeneratorTabState}
            />
          )}
          {selectedTab.tab === 'interactiveElements' && (
            <InteractiveElementsTab
              handleRoundTypeChanged={handleSlideTypeChanged}
              onSlideChange={onSlideChange}
              rounds={slides ? slides : []}
              slide={selectedSlide}
              isGenerator={!!aiContent?.prompts?.length}
              onAllRoundChange={onAllRoundChange}
              templateId={templateId}
              setSelectedElements={setSelectedElements}
            />
          )}
          {selectedTab.tab === 'media' && (
            <MediaTab
              templateId={templateId}
              onRoundChange={onSlideChange}
              tabState={selectedTab}
              round={selectedSlide}
              onDroppedImageDataConsumed={onDroppedImageDataConsumed}
              droppedImageData={droppedImageData}
              onElementsChange={onElementsChange}
              selectedElements={selectedElements}
            />
          )}
          {selectedTab.tab === 'elements' && (
            <FigureElementTab onRoundChange={onSlideChange} round={selectedSlide} />
          )}
          {selectedTab.tab === 'background' && (
            <BackgroundTab
              onRoundChange={onSlideChange}
              round={selectedSlide}
              setBackgroundForAllRounds={setBackgroundForAllRounds}
            />
          )}
          {selectedTab.tab === 'uploads' && (
            <UploadsTab
              onAddActivity={onAddSlide}
              templateId={templateId}
              setUploadedSlidesCache={setUploadedSlidesCache}
            />
          )}
          {selectedTab.tab === 'templates' && (
            <TemplatesTab
              onAddActivity={onAddSlide}
              setSearchTemplatesValue={setSearchTemplatesValue}
              searchTemplatesValue={searchTemplatesValue}
              templateId={templateId}
            />
          )}
          {selectedTab.tab === 'translate' && (
            <TranslateTab
              selectedSlide={selectedSlide}
              slides={slides}
              onAddTranslatedSlides={addTranslatedSlides}
              lessonAttachments={lessonAttachments}
            />
          )}
          {selectedTab.tab === 'colorPicker' && (
            <ColorPickerTab
              onElementsChange={onElementsChange}
              onRoundChange={onSlideChange}
              selectedElements={selectedElements}
              tabState={selectedTab}
              round={selectedSlide}
            />
          )}
          {selectedTab.tab === 'text' && (
            <TextTab onRoundChange={onSlideChange} selectedSlide={selectedSlide} />
          )}
        </TabPaneContainer>
      )}
      {!isDrawerOpen && (
        <div style={{ width: '100%' }} />
      ) /* Just to have a component that fills the remaining height while animating the height */}
      <TabsContainer>
        {tabSelector.newSlideSelector.map((button, i) => {
          return (
            <TabSelectorButton
              key={i}
              type="ghost"
              className="editorTab"
              icon={button.icon}
              $active={selectedTab.tab === button.tabTitle}
              onClick={() => {
                handleTabChange(button);
              }}
            >
              {t(button.displayTitle)}
            </TabSelectorButton>
          );
        })}
        {tabSelector.editorSelector.map((button, i) => {
          return (
            <TabSelectorButton
              key={i}
              type="ghost"
              icon={button.icon}
              className="editorTab"
              $active={selectedTab.tab === button.tabTitle}
              onClick={() => {
                handleTabChange(button);
              }}
            >
              {t(button.displayTitle)}
            </TabSelectorButton>
          );
        })}
      </TabsContainer>
    </FloatingContainer>
  );
};

export default DesignerMobileTabs;

const TabPaneContainer = styled.div`
  width: 100%;
  overflow-y: hidden;
`;
const TabsContainer = styled.div`
  display: flex;
  width: 100%;
  height: 70px;
  overflow-y: hidden;
`;

const FloatingContainer = styled(motion.div)`
  z-index: ${zIndexes('CREATOR_DRAWER')};
  width: 100%;
  background-color: white;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  border-radius: ${theme.effects.floatingCreatorElementBorderRadius};
  overflow-x: scroll;
  overflow-y: hidden;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }

  display: grid;
  grid-template-rows: auto 70px;

  position: absolute;
  bottom: 0px;
  left: 0px;
  width: calc(100% - 12px);
  margin: 0px 6px 6px 6px;

  /* To make framer motion work */
  touch-action: none;
`;
